import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { startAutoUpdate } from './autoUpdate';
import { installAuthChangeFix } from './authChange';
import { captureAttribution } from './attribution';

// Where this visitor came from. Must run before anything mints the visitor ID,
// so it can tell a brand-new browser from a returning one.
captureAttribution();
// Self-hosted @font-face for the Fontshare families — imported before global.css
// so the faces are declared before any styles reference them. Bundled by Vite
// (fingerprinted, immutable cache) instead of fetched from cdn.fontshare.com.
import './fonts.css';
import './global.css';

// Self-heal a stale deploy. When a dynamically imported route chunk fails to
// load — its hashed filename changed in a newer deploy while this tab still
// held the old HTML and chunk map — Vite fires `vite:preloadError` on window.
// Left unhandled, the failed import rejects and the route hangs on its loading
// spinner (exactly the "refreshed and still spinning" symptom after a deploy).
// Reload once to pull the fresh HTML + new chunks. A short time-based guard in
// sessionStorage prevents a reload loop if an asset is genuinely unreachable
// (offline, or a real 404), while still letting a LATER deploy self-heal in the
// same tab.
const RELOAD_GUARD_KEY = 'entredash.preloadReloadedAt';
window.addEventListener('vite:preloadError', () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || '0');
    if (Date.now() - last < 10000) return; // at most one reload per 10s
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode edge) — still worth one reload.
  }
  window.location.reload();
});

// Proactively keep this tab on the latest deploy: notice when a newer build is
// live and reload onto it at a safe moment, so an open tab never goes stale.
// Production only — dev has HMR, and dev's non-hashed asset URLs make the check
// inert anyway.
if (import.meta.env.PROD) {
  startAutoUpdate();
}

// Repair the platform SDK's broken email/phone change flow before anything can
// trigger it (see ./authChange). Must run before the first auth-change request.
installAuthChangeFix();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Catch any uncaught render error so a single broken component shows a
        recoverable fallback instead of blanking the whole app. */}
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);

// Service worker: RETIRED. This app used to register a shell-caching worker
// (/sw.js) for PWA install + instant repeat-load. Under frequent deploys that
// cache stranded returning users on stale JS chunks ("Failed to fetch
// dynamically imported module"), broke logout, and hung fresh page loads —
// it sat in front of the deploy self-heal above and defeated it. We no longer
// register a worker.
//
// Proactively unregister any worker still installed from a prior version so
// those browsers recover on this load. The kill-switch /sw.js handles browsers
// that never get far enough to run this code (it clears caches, unregisters
// itself, and reloads open tabs on its next update check). Together these two
// paths converge on "no service worker, fresh assets" with no reload loop,
// since this branch never re-registers anything.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => {
      for (const reg of regs) reg.unregister();
    })
    .catch(() => {
      // Non-fatal — nothing to clean up, or the API is unavailable.
    });
}
